import React from 'react'
import {
  RealEstateCategory,
  RealEstateFeatures,
  Salutation,
  SegmentationType,
  TransactionType,
} from '../../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../../@types/flow-types'
import { PROPERTY_OWNER_APARTMENT_SELL_FLOW_PATH } from '../../../../../../services/routes/routes'
import { priceToApiSell } from '../../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import { webViewPropertyOwnerApartmentSellSchema } from './webViewPropertyOwnerApartmentSellSchema'
import {
  SCREEN_ADDITIONAL_INFORMATION_CTA,
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OBJECT_DETAILS,
  SCREEN_REALTOR_LEAD_ENGINE,
  SCREEN_WEB_VIEW_AMENITIES,
  SCREENS,
} from '../../../../../../services/domain/wizardScreens'
import Address from '../../../../../../components/pages/insertionPage/screens/address/Address'
import BasicInfo from '../../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  livingSpaceField,
  roomField,
  startRentalDateWithToggleField,
} from '../../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import Description from '../../../../../../components/pages/insertionPage/screens/description/Description'
import { AdditionalInformationCta } from '../../../../../../components/pages/insertionPage/screens/objectDetailsCta/AdditionalInformationCta'
import ContactInfo from '../../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import { WebViewMultimedia } from '../../../../../../components/pages/insertionPage/screens/multimediaNew/webView/WebViewMultimedia'
import { MultimediaUploaderType } from '../../../../../../@types/multimedia-types'
import { ObjectDetails } from '../../../../../../components/pages/insertionPage/screens/objectDetails/ObjectDetails'
import { buildingTypeField } from '../../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import {
  bathroomField,
  estateFeatures,
  interiorQualityField,
  noBedrooms,
  usableAreaField,
  webViewParkingSpace,
} from '../../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import {
  floorNumberField,
  totalFloorsField,
} from '../../../../../../components/pages/insertionPage/screens/objectDetails/wrappers/objectDetailsFieldsWrapper'
import WebViewConstruction from '../../../../../../components/pages/insertionPage/screens/construction/webView/WebViewConstruction'
import WebViewAmenities from '../../../../../../components/pages/insertionPage/screens/otherFacilities/webView/WebViewAmenities'
import { AmenitiesLabel } from '../../../../../../components/pages/insertionPage/screens/otherFacilities/webView/AmenitiesLabel'
import { apartmentSellBuildingTypeOptions } from '../common'
import { getDefaultClasses } from '../../../../../../utils/style-utils'
import { RealtorLeadEngine } from '../../../../../../components/pages/insertionPage/screens/realtorLeadEngine/RealtorLeadEngine'
import WebViewCostsSell from '../../../../../../components/pages/insertionPage/screens/costs/wrappers/webView/WebViewCostsSell'

const webViewPropertyOwnerApartmentSellFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <WebViewMultimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[MultimediaUploaderType.PICTURE]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} isPriceOverviewLinkDisplayed={false} isInternationalAddressOption={true} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        properties={[
          livingSpaceField('full', undefined, true),
          roomField('one-whole margin-top-l', 'wizard.screen.basicInfo.room.label', true),
          startRentalDateWithToggleField('margin-top-l grid-item one-whole'),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_COSTS],
    element: (props) => <WebViewCostsSell {...props} />,
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => (
      <Description
        {...props}
        hasFurnishingNote={true}
        hasLocationNote={true}
        hasOtherNote={true}
        hasHomeStreetHome={false}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_REALTOR_LEAD_ENGINE],
    element: (props) => <RealtorLeadEngine {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
  {
    screen: SCREENS[SCREEN_ADDITIONAL_INFORMATION_CTA],
    element: () => <AdditionalInformationCta screens={['objectDetails', 'construction', 'webViewAmenities']} />,
  },
  {
    screen: SCREENS[SCREEN_OBJECT_DETAILS],
    element: (props) => (
      <ObjectDetails
        {...props}
        properties={[
          buildingTypeField('apartment', apartmentSellBuildingTypeOptions, getDefaultClasses('full')),
          usableAreaField(undefined, getDefaultClasses()),
          bathroomField(getDefaultClasses()),
          noBedrooms('full'),
          floorNumberField(getDefaultClasses('full')),
          totalFloorsField(getDefaultClasses('full'), undefined, 'wizard.screen.objectDetails.totalFloors.tooltip'),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => (
      <WebViewConstruction {...props} hasLastModernizationField={true} hasConstructionPhase={false} />
    ),
  },
  {
    screen: SCREENS[SCREEN_WEB_VIEW_AMENITIES],
    element: (props) => (
      <WebViewAmenities
        {...props}
        properties={[
          createApartmentSellEstateFeaturesForWebView(
            <AmenitiesLabel
              tooltipContentKey={'wizard.screen.otherFacilities.amenitiesPopover.apartmentSellContent'}
            />,
            'grid-item one-whole'
          ),
          interiorQualityField('grid-item one-whole margin-top-l'),
          webViewParkingSpace('grid-item one-whole margin-top-xl'),
        ]}
      />
    ),
  },
]

export const apartmentSellEstateFeaturesListForWebView = [
  RealEstateFeatures.EQUIPPED_KITCHEN,
  RealEstateFeatures.BALCONY_TERRACE,
  RealEstateFeatures.GUEST_TOILET,
  RealEstateFeatures.SHARED_GARDEN,
  RealEstateFeatures.ELEVATOR,
  RealEstateFeatures.BASEMENT,
  RealEstateFeatures.STEPLESS_ACCESSIBLE,
  RealEstateFeatures.SUMMER_RESIDENCE_PRACTICAL,
  RealEstateFeatures.MONUMENT,
]

export const createApartmentSellEstateFeaturesForWebView = (label: React.ReactNode, className: string) =>
  estateFeatures(apartmentSellEstateFeaturesListForWebView, label, className)

export const webViewPropertyOwnerApartmentSellFlow: Flow = {
  flowInformation: {
    isMobileFlow: true,
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.apartment',
    offerType: 'wizard.navigation.header.sells',
    path: PROPERTY_OWNER_APARTMENT_SELL_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_APARTMENT_SELL,
    validationSchema: webViewPropertyOwnerApartmentSellSchema,
    priceToApiMapper: priceToApiSell,
    options: {
      hasTextSuggestions: true,
    },
    metaData: {
      category: RealEstateCategory.APARTMENT,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.SELL,
    },
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
    rented: false,
    unknownConstructionYear: false,
    salutation: Salutation.NO_SALUTATION,
  },
  flowScreens: webViewPropertyOwnerApartmentSellFlowScreens,
}
